import { useEffect, useState } from 'react';
import School from '../models/School';

// Upcoming portrait days for ONE school, for the pre-picture-day notice on the
// access-code entry form.
//
// Fetched per selected school rather than carried on the school list: the
// dropdown loads ~13k schools and this costs a joined djobs query each, so the
// server keeps it behind selectExtra (see Api::SchoolResource#picture_days).
//
// `loading` matters as much as the data. The entry form decides whether to
// SHOW the email and access-code fields based on the answer, so between
// picking a school and hearing back there is no correct thing to render: guess
// "show" and a pre-season school's fields appear and are then yanked away,
// which reads as the page breaking. The form holds the fields back while this
// is true, so they only ever appear once, and stay.
export default function usePictureDays(schoolId) {
  const [pictureDays, setPictureDays] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Clear on deselect, so a notice for the previous school can't linger over
    // an empty School field.
    if (!schoolId) {
      setPictureDays(null);
      setLoading(false);
      return undefined;
    }

    // A parent correcting a mis-click changes school faster than the requests
    // come back, and responses are not guaranteed to arrive in the order they
    // were sent -- without this flag a slow reply for the ABANDONED school
    // could land last and leave its dates on screen under the new school's
    // name. The cleanup runs before the next effect, so only the newest
    // request is ever allowed to set state.
    let cancelled = false;

    setPictureDays(null);
    setLoading(true);

    School
      .selectExtra(['pictureDays'])
      .find(schoolId)
      .then((res) => {
        if (cancelled) return;
        setPictureDays(res.data.pictureDays || null);
        setLoading(false);
      })
      .catch(() => {
        // The notice is an enhancement, not a gate. If the lookup fails the
        // form still works exactly as it did before it existed -- clearing
        // `loading` is what lets the fields through, so a parent is never
        // stranded on a school-only screen by a failed request.
        if (cancelled) return;
        setPictureDays(null);
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [schoolId]);

  return { pictureDays, loading };
}
